import type { Dispatch, SetStateAction } from 'react';

export const getRandomElement = <T>(arr: T[]): T => {
  return arr[Math.floor(Math.random() * arr.length)];
};

/**
 * 주어진 배열 내에서 특정 요소가 맨 앞으로 위치하도록 정렬 (원본 변경 X)
 */
export const prioritizeElementInArray = <T>(arr: T[], prioritizedElement: T) => {
  return [...arr].sort((a, b) => {
    if (a === prioritizedElement) return -1;
    if (b === prioritizedElement) return 1;
    return 0;
  });
};

/**
 * 주어진 배열을 기준으로 함수가 true를 반환하는 요소와 false를 반환하는 요소로 나누어 반환
 * validator에 부합하는 요소들이 첫 번째 배열에, 부합하지 않는 요소들이 두 번째 배열에 위치
 */
export const partition = <T>(array: T[], validator: (element: T) => boolean): [T[], T[]] => {
  return array.reduce<[T[], T[]]>(
    ([pass, fail], elem) => {
      return validator(elem) ? [[...pass, elem], fail] : [pass, [...fail, elem]];
    },
    [[], []]
  );
};

/**
 * 주어진 배열에서 특정 키(predicate)를 기준으로 중복된 요소를 제거한 새로운 배열을 반환합니다.
 * 각 키 값에 대해 첫 번째로 나타나는 요소만 유지됩니다.
 * @template T - 배열 요소의 타입
 * @param {T[]} array - 처리할 원본 배열
 * @param {keyof T} predicate - 중복 여부를 판단할 기준이 되는 요소의 키
 * @returns {T[]} - 중복이 제거된 새로운 배열
 */
export const uniqBy = <T>(array: T[], predicate: keyof T): T[] => {
  const seen = new Set<T[keyof T]>();
  const result: T[] = [];

  for (const element of array) {
    const value = element[predicate];

    if (!seen.has(value)) {
      seen.add(value);
      result.push(element);
    }
  }

  return result;
};

/**
 * 주어진 배열에서 이전 인덱스와 다른 랜덤한 인덱스를 생성하여 상태를 업데이트하는 함수를 반환합니다.
 * React의 setState를 사용하여 현재 인덱스 상태를 새로운 랜덤 인덱스로 업데이트합니다.
 * @template T - 배열 요소의 타입
 * @param data - 인덱스를 생성할 대상 배열
 * @param setState - 인덱스 상태를 업데이트할 React setState 함수
 * @returns 호출 시 새로운 랜덤 인덱스로 상태를 업데이트하는 함수
 * @example
 * const [currentIndex, setCurrentIndex] = useState(0);
 * const fruits = ['apple', 'banana', 'orange'];
 * const shuffleIndex = sampleRandomIndex(fruits, setCurrentIndex);
 * shuffleIndex(); // currentIndex가 이전 값과 다른 랜덤한 인덱스로 업데이트됨
 */
export const sampleRandomIndex =
  <T>(data: T[], setState: Dispatch<SetStateAction<number>>) =>
  () => {
    if (data.length === 0) return;

    setState((prev) => {
      let next = 0;

      do {
        next = Math.floor(Math.random() * data.length);
      } while (next === prev);

      return next;
    });
  };
